/**
 * Fullscreen-Onboarding-Wizard für CP-3.
 *
 * 12 Schritte, frei navigierbar (kein erzwungener Forward-Flow), jeder
 * Schritt darf übersprungen werden. Antworten werden per debouncedem
 * Auto-Save (~700ms) nach Firestore geschrieben — der Statustext
 * unten links bestätigt jede erfolgreiche Sicherung. Am Ende
 * komprimiert Gemini die Antworten in `phoneSettings.knowledgeBase`,
 * `introPhrase`, `outroPhrase` und `promptConversationStyle`.
 */
import * as React from 'react';
import {
  Building2,
  Calendar as CalendarIcon,
  Check,
  CheckCircle2,
  Clock,
  FileText,
  HelpCircle,
  Loader2,
  MapPin,
  Plus,
  Sparkle,
  Sparkles,
  Trash2,
  Upload,
  Wand2,
  X,
  AlertTriangle,
  Coins,
  ListChecks,
  UserCog,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input, Textarea } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { useToast } from '../ui/toast';
import { cn } from '../../lib/utils';
import {
  useCompressOnboarding,
  useDeleteOnboardingDoc,
  useOnboarding,
  useSaveOnboarding,
  useUploadOnboardingDoc,
} from '../../hooks/useOnboarding';
import type {
  OnboardingData,
  OnboardingDocument,
  OnboardingFAQ,
  OnboardingOffering,
  OnboardingOpeningHour,
  OnboardingSaveInput,
  OnboardingSpecialClosure,
} from '@cp3/shared';

interface Props {
  open: boolean;
  onClose: () => void;
}

const WEEKDAYS: ReadonlyArray<{ idx: 0 | 1 | 2 | 3 | 4 | 5 | 6; label: string }> = [
  { idx: 1, label: 'Montag' },
  { idx: 2, label: 'Dienstag' },
  { idx: 3, label: 'Mittwoch' },
  { idx: 4, label: 'Donnerstag' },
  { idx: 5, label: 'Freitag' },
  { idx: 6, label: 'Samstag' },
  { idx: 0, label: 'Sonntag' },
];

interface StepDef {
  key: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const STEPS: ReadonlyArray<StepDef> = [
  { key: 'company', label: 'Unternehmen', icon: Building2 },
  { key: 'contact', label: 'Kontakt', icon: MapPin },
  { key: 'hours', label: 'Öffnungszeiten', icon: Clock },
  { key: 'closures', label: 'Sonderzeiten', icon: CalendarIcon },
  { key: 'offerings', label: 'Angebot', icon: ListChecks },
  { key: 'pricing', label: 'Preise', icon: Coins },
  { key: 'booking', label: 'Buchung', icon: CalendarIcon },
  { key: 'faq', label: 'FAQ', icon: HelpCircle },
  { key: 'emergency', label: 'Notfälle', icon: AlertTriangle },
  { key: 'documents', label: 'Dokumente', icon: FileText },
  { key: 'personality', label: 'Persönlichkeit', icon: UserCog },
  { key: 'summary', label: 'Zusammenfassung', icon: Sparkles },
];

function emptyState(): OnboardingData {
  return {
    openingHours: WEEKDAYS.map((d) => ({
      weekday: d.idx,
      closed: d.idx === 0 || d.idx === 6,
      openTime: d.idx === 0 || d.idx === 6 ? undefined : '09:00',
      closeTime: d.idx === 0 || d.idx === 6 ? undefined : '18:00',
    })),
    pricingPolicy: 'mention_range',
    bookingMode: 'direct',
    addressing: 'sie',
    tonality: 'casual',
    consultativeMode: false,
  };
}

export function OnboardingWizard({ open, onClose }: Props) {
  const onboarding = useOnboarding();
  const save = useSaveOnboarding();
  const compress = useCompressOnboarding();
  const { toast } = useToast();

  const [activeIdx, setActiveIdx] = React.useState(0);
  const [form, setForm] = React.useState<OnboardingData>(emptyState());
  const [hydrated, setHydrated] = React.useState(false);
  const [savedAt, setSavedAt] = React.useState<number | null>(null);

  // Hydrate once from server.
  React.useEffect(() => {
    if (!open || hydrated) return;
    const server = onboarding.data?.data;
    if (server) {
      setForm({ ...emptyState(), ...server });
    }
    if (onboarding.isSuccess) setHydrated(true);
  }, [open, hydrated, onboarding.data, onboarding.isSuccess]);

  // Reset hydration when dialog closes so a re-open re-fetches.
  React.useEffect(() => {
    if (!open) {
      setHydrated(false);
      setActiveIdx(0);
    }
  }, [open]);

  // Debounced auto-save: kick a 700 ms timer on every form change.
  // Skipping the very first render after hydration so we don't redundantly
  // POST the server's own snapshot back to itself.
  const firstSaveSkippedRef = React.useRef(false);
  React.useEffect(() => {
    if (!hydrated) return;
    if (!firstSaveSkippedRef.current) {
      firstSaveSkippedRef.current = true;
      return;
    }
    const t = window.setTimeout(() => {
      const payload = toSaveInput(form);
      save.mutate(payload, {
        onSuccess: () => setSavedAt(Date.now()),
      });
    }, 700);
    return () => window.clearTimeout(t);
    // We deliberately depend on the JSON string of `form` so per-character
    // edits don't fire a save burst — JSON.stringify is fine here, the
    // doc is tiny (<10kb).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(form), hydrated]);

  function update<K extends keyof OnboardingData>(key: K, value: OnboardingData[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function applyCompression() {
    try {
      // Force-flush pending auto-save before kicking compression so Gemini
      // sees the latest answers, not yesterday's snapshot.
      await save.mutateAsync(toSaveInput(form));
      const r = await compress.mutateAsync();
      toast({
        title: 'Übernommen',
        description: `Wissensbasis (${r.knowledgeBase.length.toLocaleString('de-DE')} Z), Begrüßung, Verabschiedung und Konversations-Stil sind jetzt im KI-Modell-Tab gefüllt.`,
      });
      onClose();
    } catch (err) {
      toast({
        title: 'Komprimierung fehlgeschlagen',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  const documents = onboarding.data?.documents ?? [];
  const activeStep = STEPS[activeIdx]!;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex h-[95vh] max-h-[95vh] w-[95vw] max-w-[1400px] flex-col p-0">
        <DialogHeader className="border-b border-border px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Wand2 className="h-5 w-5 text-primary" />
              Onboarding · {activeStep.label}
            </DialogTitle>
            <SaveStatus savedAt={savedAt} saving={save.isPending} />
          </div>
        </DialogHeader>

        <div className="flex flex-1 min-h-0">
          {/* Sidebar — clickable steps */}
          <aside className="hidden w-64 shrink-0 overflow-y-auto border-r border-border bg-muted/20 p-3 md:block">
            <ol className="space-y-1">
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                const active = i === activeIdx;
                const done = (form.completedSteps ?? []).includes(i);
                return (
                  <li key={s.key}>
                    <button
                      type="button"
                      onClick={() => setActiveIdx(i)}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors',
                        active
                          ? 'bg-primary/10 font-medium text-foreground'
                          : 'text-muted-foreground hover:bg-secondary/40 hover:text-foreground',
                      )}
                    >
                      <span
                        className={cn(
                          'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold',
                          active
                            ? 'bg-primary text-primary-foreground'
                            : done
                              ? 'bg-emerald-500/20 text-emerald-600'
                              : 'bg-muted text-muted-foreground',
                        )}
                      >
                        {done ? <Check className="size-3" /> : i + 1}
                      </span>
                      <Icon className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{s.label}</span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </aside>

          {/* Mobile-Tab-bar */}
          <div className="md:hidden">{/* (Could grow into a horizontal scroll). */}</div>

          {/* Step content */}
          <main className="flex flex-1 min-h-0 flex-col">
            <div className="flex-1 overflow-y-auto px-6 py-6">
              {!hydrated ? (
                <div className="space-y-3">
                  <Skeleton className="h-8 w-1/3" />
                  <Skeleton className="h-32 w-full" />
                  <Skeleton className="h-32 w-full" />
                </div>
              ) : (
                <StepContent
                  step={activeStep}
                  form={form}
                  update={update}
                  setForm={setForm}
                  documents={documents}
                  onApply={() => void applyCompression()}
                  applyPending={compress.isPending}
                />
              )}
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-border bg-card px-6 py-3">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setActiveIdx((i) => Math.max(0, i - 1))}
                disabled={activeIdx === 0}
              >
                ← Zurück
              </Button>
              <div className="text-xs text-muted-foreground">
                Schritt {activeIdx + 1} von {STEPS.length} · überspringbar
              </div>
              {activeIdx < STEPS.length - 1 ? (
                <Button
                  size="sm"
                  onClick={() => {
                    // Mark current step as completed when navigating forward.
                    setForm((p) => ({
                      ...p,
                      completedSteps: Array.from(
                        new Set([...(p.completedSteps ?? []), activeIdx]),
                      ),
                    }));
                    setActiveIdx((i) => Math.min(STEPS.length - 1, i + 1));
                  }}
                >
                  Weiter →
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={() => void applyCompression()}
                  disabled={compress.isPending}
                >
                  {compress.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkle className="mr-2 h-4 w-4" />
                  )}
                  Komprimieren & übernehmen
                </Button>
              )}
            </div>
          </main>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────

function SaveStatus({ savedAt, saving }: { savedAt: number | null; saving: boolean }) {
  if (saving) {
    return (
      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Sichere …
      </span>
    );
  }
  if (savedAt) {
    return (
      <span className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400">
        <CheckCircle2 className="h-3.5 w-3.5" />
        Daten sind gesichert
      </span>
    );
  }
  return (
    <span className="text-xs text-muted-foreground">Eingaben werden automatisch gesichert</span>
  );
}

function toSaveInput(d: OnboardingData): OnboardingSaveInput {
  // Strip undefined fields so the server-side Zod doesn't see noise.
  const out: Partial<OnboardingSaveInput> = {};
  if (d.companyName !== undefined) out.companyName = d.companyName;
  if (d.industry !== undefined) out.industry = d.industry;
  if (d.ownerName !== undefined) out.ownerName = d.ownerName;
  if (d.companyDescription !== undefined) out.companyDescription = d.companyDescription;
  if (d.addressStreet !== undefined) out.addressStreet = d.addressStreet;
  if (d.addressZip !== undefined) out.addressZip = d.addressZip;
  if (d.addressCity !== undefined) out.addressCity = d.addressCity;
  if (d.addressCountry !== undefined) out.addressCountry = d.addressCountry;
  if (d.publicEmail !== undefined) out.publicEmail = d.publicEmail;
  if (d.website !== undefined) out.website = d.website;
  if (d.openingHours !== undefined) out.openingHours = d.openingHours;
  if (d.specialClosures !== undefined) out.specialClosures = d.specialClosures;
  if (d.emergencyContact !== undefined) out.emergencyContact = d.emergencyContact;
  if (d.offerings !== undefined) out.offerings = d.offerings;
  if (d.pricingPolicy !== undefined) out.pricingPolicy = d.pricingPolicy;
  if (d.pricingNotes !== undefined) out.pricingNotes = d.pricingNotes;
  if (d.bookingMode !== undefined) out.bookingMode = d.bookingMode;
  if (d.bookingNotes !== undefined) out.bookingNotes = d.bookingNotes;
  if (d.faq !== undefined) out.faq = d.faq;
  if (d.emergencyDefinition !== undefined) out.emergencyDefinition = d.emergencyDefinition;
  if (d.emergencyHandling !== undefined) out.emergencyHandling = d.emergencyHandling;
  if (d.documentIds !== undefined) out.documentIds = d.documentIds;
  if (d.addressing !== undefined) out.addressing = d.addressing;
  if (d.tonality !== undefined) out.tonality = d.tonality;
  if (d.consultativeMode !== undefined) out.consultativeMode = d.consultativeMode;
  if (d.completedSteps !== undefined) out.completedSteps = d.completedSteps;
  return out as OnboardingSaveInput;
}

// ── Step content router ────────────────────────────────────────────────────

interface StepProps {
  step: StepDef;
  form: OnboardingData;
  update: <K extends keyof OnboardingData>(key: K, value: OnboardingData[K]) => void;
  setForm: React.Dispatch<React.SetStateAction<OnboardingData>>;
  documents: OnboardingDocument[];
  onApply: () => void;
  applyPending: boolean;
}

function StepContent({ step, form, update, setForm, documents, onApply, applyPending }: StepProps) {
  switch (step.key) {
    case 'company':
      return <CompanyStep form={form} update={update} />;
    case 'contact':
      return <ContactStep form={form} update={update} />;
    case 'hours':
      return <HoursStep form={form} setForm={setForm} />;
    case 'closures':
      return <ClosuresStep form={form} update={update} setForm={setForm} />;
    case 'offerings':
      return <OfferingsStep form={form} setForm={setForm} />;
    case 'pricing':
      return <PricingStep form={form} update={update} />;
    case 'booking':
      return <BookingStep form={form} update={update} />;
    case 'faq':
      return <FAQStep form={form} setForm={setForm} />;
    case 'emergency':
      return <EmergencyStep form={form} update={update} />;
    case 'documents':
      return <DocumentsStep documents={documents} />;
    case 'personality':
      return <PersonalityStep form={form} update={update} />;
    case 'summary':
      return <SummaryStep form={form} documents={documents} onApply={onApply} pending={applyPending} />;
    default:
      return null;
  }
}

// ── Steps ──────────────────────────────────────────────────────────────────

function StepHeader({ title, description }: { title: string; description: string }) {
  return (
    <div className="mb-6 space-y-1">
      <h2 className="text-2xl font-semibold">{title}</h2>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  );
}

function CompanyStep({ form, update }: { form: OnboardingData; update: StepProps['update'] }) {
  return (
    <div className="max-w-2xl space-y-5">
      <StepHeader
        title="Über das Unternehmen"
        description="Diese Angaben verwendet die KI als Identität in Begrüßung und Verabschiedung."
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="ob-company">Firmenname</Label>
          <Input
            id="ob-company"
            value={form.companyName ?? ''}
            placeholder="z.B. Friseursalon Beispiel"
            onChange={(e) => update('companyName', e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ob-industry">Branche</Label>
          <Input
            id="ob-industry"
            value={form.industry ?? ''}
            placeholder="z.B. Friseur, Arztpraxis, Restaurant"
            onChange={(e) => update('industry', e.target.value)}
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="ob-owner">Inhaber/in (optional)</Label>
        <Input
          id="ob-owner"
          value={form.ownerName ?? ''}
          placeholder="z.B. Anna Beispiel"
          onChange={(e) => update('ownerName', e.target.value)}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="ob-desc">Kurzbeschreibung (1-2 Sätze)</Label>
        <Textarea
          id="ob-desc"
          rows={3}
          value={form.companyDescription ?? ''}
          placeholder="Was macht ihr und für wen?"
          onChange={(e) => update('companyDescription', e.target.value)}
        />
      </div>
    </div>
  );
}

function ContactStep({ form, update }: { form: OnboardingData; update: StepProps['update'] }) {
  return (
    <div className="max-w-2xl space-y-5">
      <StepHeader
        title="Kontakt & Adresse"
        description="Wird in die Wissensbasis übernommen, falls die KI nach Anfahrt oder Erreichbarkeit gefragt wird."
      />
      <div className="grid gap-4 sm:grid-cols-[2fr_1fr]">
        <div className="space-y-1.5">
          <Label htmlFor="ob-street">Straße + Nummer</Label>
          <Input
            id="ob-street"
            value={form.addressStreet ?? ''}
            onChange={(e) => update('addressStreet', e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ob-zip">PLZ</Label>
          <Input
            id="ob-zip"
            value={form.addressZip ?? ''}
            onChange={(e) => update('addressZip', e.target.value)}
          />
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="ob-city">Ort</Label>
          <Input
            id="ob-city"
            value={form.addressCity ?? ''}
            onChange={(e) => update('addressCity', e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ob-country">Land</Label>
          <Input
            id="ob-country"
            value={form.addressCountry ?? ''}
            placeholder="Österreich, Deutschland, Schweiz …"
            onChange={(e) => update('addressCountry', e.target.value)}
          />
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="ob-email">Öffentliche E-Mail</Label>
          <Input
            id="ob-email"
            type="email"
            value={form.publicEmail ?? ''}
            onChange={(e) => update('publicEmail', e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ob-web">Website</Label>
          <Input
            id="ob-web"
            value={form.website ?? ''}
            placeholder="https://..."
            onChange={(e) => update('website', e.target.value)}
          />
        </div>
      </div>
    </div>
  );
}

function HoursStep({
  form,
  setForm,
}: {
  form: OnboardingData;
  setForm: React.Dispatch<React.SetStateAction<OnboardingData>>;
}) {
  const hours = form.openingHours ?? [];
  function patch(idx: number, patch: Partial<OnboardingOpeningHour>) {
    setForm((p) => ({
      ...p,
      openingHours: (p.openingHours ?? []).map((h, i) => (i === idx ? { ...h, ...patch } : h)),
    }));
  }
  return (
    <div className="max-w-3xl space-y-4">
      <StepHeader
        title="Öffnungszeiten"
        description="Pro Wochentag: geschlossen, oder Öffnungs- und Schlusszeit (optional eine Mittagspause)."
      />
      <div className="space-y-2">
        {WEEKDAYS.map(({ idx, label }) => {
          const i = hours.findIndex((h) => h.weekday === idx);
          const h = i >= 0 ? hours[i]! : { weekday: idx, closed: true };
          const realIdx = i >= 0 ? i : -1;
          return (
            <div
              key={idx}
              className={cn(
                'rounded-md border p-3 transition-colors',
                h.closed ? 'border-border bg-muted/20' : 'border-primary/40 bg-primary/5',
              )}
            >
              <div className="flex flex-wrap items-center gap-3">
                <div className="w-28 font-medium">{label}</div>
                <label className="flex items-center gap-2 text-sm">
                  <Switch
                    checked={!h.closed}
                    onCheckedChange={(checked) => {
                      if (realIdx < 0) {
                        setForm((p) => ({
                          ...p,
                          openingHours: [
                            ...(p.openingHours ?? []),
                            { weekday: idx, closed: !checked, openTime: '09:00', closeTime: '18:00' },
                          ],
                        }));
                      } else {
                        patch(realIdx, { closed: !checked });
                      }
                    }}
                  />
                  {h.closed ? 'geschlossen' : 'geöffnet'}
                </label>
                {!h.closed && (
                  <>
                    <Input
                      type="time"
                      value={h.openTime ?? ''}
                      onChange={(e) => patch(realIdx, { openTime: e.target.value })}
                      className="w-28"
                    />
                    <span className="text-muted-foreground">bis</span>
                    <Input
                      type="time"
                      value={h.closeTime ?? ''}
                      onChange={(e) => patch(realIdx, { closeTime: e.target.value })}
                      className="w-28"
                    />
                    <div className="text-xs text-muted-foreground">Pause:</div>
                    <Input
                      type="time"
                      value={h.breakStart ?? ''}
                      onChange={(e) => patch(realIdx, { breakStart: e.target.value })}
                      className="w-24"
                    />
                    <span className="text-muted-foreground">–</span>
                    <Input
                      type="time"
                      value={h.breakEnd ?? ''}
                      onChange={(e) => patch(realIdx, { breakEnd: e.target.value })}
                      className="w-24"
                    />
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ClosuresStep({
  form,
  update,
  setForm,
}: {
  form: OnboardingData;
  update: StepProps['update'];
  setForm: React.Dispatch<React.SetStateAction<OnboardingData>>;
}) {
  const closures = form.specialClosures ?? [];
  function add() {
    setForm((p) => ({
      ...p,
      specialClosures: [...(p.specialClosures ?? []), { label: '' }],
    }));
  }
  function remove(i: number) {
    setForm((p) => ({
      ...p,
      specialClosures: (p.specialClosures ?? []).filter((_, j) => j !== i),
    }));
  }
  function patch(i: number, patch: Partial<OnboardingSpecialClosure>) {
    setForm((p) => ({
      ...p,
      specialClosures: (p.specialClosures ?? []).map((c, j) => (i === j ? { ...c, ...patch } : c)),
    }));
  }
  return (
    <div className="max-w-3xl space-y-5">
      <StepHeader
        title="Sonderzeiten & Feiertage"
        description="Geschlossene Zeiträume (Sommerpause, Weihnachten, Feiertage). Optional ein Notfallkontakt für dringliche Fälle."
      />
      <div className="space-y-3">
        {closures.map((c, i) => (
          <div key={i} className="rounded-md border border-border p-3">
            <div className="grid gap-3 sm:grid-cols-[2fr_1fr_1fr_auto]">
              <Input
                placeholder="z.B. Weihnachten, Sommerpause"
                value={c.label}
                onChange={(e) => patch(i, { label: e.target.value })}
              />
              <Input
                type="date"
                value={c.fromDate ?? ''}
                onChange={(e) => patch(i, { fromDate: e.target.value })}
              />
              <Input
                type="date"
                value={c.toDate ?? ''}
                onChange={(e) => patch(i, { toDate: e.target.value })}
              />
              <Button size="icon" variant="ghost" onClick={() => remove(i)}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
            <Input
              className="mt-2"
              placeholder="Notiz (optional)"
              value={c.note ?? ''}
              onChange={(e) => patch(i, { note: e.target.value })}
            />
          </div>
        ))}
        <Button size="sm" variant="outline" onClick={add}>
          <Plus className="mr-1.5 h-3.5 w-3.5" /> Sonderzeit hinzufügen
        </Button>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="ob-emergency-contact">Notfall-Vertretung (optional)</Label>
        <Input
          id="ob-emergency-contact"
          value={form.emergencyContact ?? ''}
          placeholder="z.B. Bereitschaftsarzt 089 / 1234567"
          onChange={(e) => update('emergencyContact', e.target.value)}
        />
      </div>
    </div>
  );
}

function OfferingsStep({
  form,
  setForm,
}: {
  form: OnboardingData;
  setForm: React.Dispatch<React.SetStateAction<OnboardingData>>;
}) {
  const offerings = form.offerings ?? [];
  function add() {
    setForm((p) => ({
      ...p,
      offerings: [...(p.offerings ?? []), { name: '' }],
    }));
  }
  function remove(i: number) {
    setForm((p) => ({
      ...p,
      offerings: (p.offerings ?? []).filter((_, j) => j !== i),
    }));
  }
  function patch(i: number, patch: Partial<OnboardingOffering>) {
    setForm((p) => ({
      ...p,
      offerings: (p.offerings ?? []).map((o, j) => (i === j ? { ...o, ...patch } : o)),
    }));
  }
  return (
    <div className="max-w-3xl space-y-5">
      <StepHeader
        title="Produkte & Dienstleistungen"
        description="Was bietet ihr an? Die KI nutzt die Liste, um Anrufer zu beraten, was möglich ist."
      />
      <div className="space-y-3">
        {offerings.map((o, i) => (
          <div key={i} className="rounded-md border border-border p-3 space-y-2">
            <div className="grid gap-3 sm:grid-cols-[2fr_120px_1fr_auto]">
              <Input
                placeholder="Name (z.B. Haarschnitt)"
                value={o.name}
                onChange={(e) => patch(i, { name: e.target.value })}
              />
              <Input
                type="number"
                placeholder="Min"
                value={o.durationMinutes ?? ''}
                onChange={(e) =>
                  patch(i, {
                    durationMinutes: e.target.value ? parseInt(e.target.value, 10) : undefined,
                  })
                }
              />
              <Input
                placeholder="Preis-Notiz (z.B. ab 35€)"
                value={o.priceNote ?? ''}
                onChange={(e) => patch(i, { priceNote: e.target.value })}
              />
              <Button size="icon" variant="ghost" onClick={() => remove(i)}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
            <Textarea
              rows={2}
              placeholder="Beschreibung (optional)"
              value={o.description ?? ''}
              onChange={(e) => patch(i, { description: e.target.value })}
            />
          </div>
        ))}
        <Button size="sm" variant="outline" onClick={add}>
          <Plus className="mr-1.5 h-3.5 w-3.5" /> Angebot hinzufügen
        </Button>
      </div>
    </div>
  );
}

function PricingStep({ form, update }: { form: OnboardingData; update: StepProps['update'] }) {
  return (
    <div className="max-w-2xl space-y-5">
      <StepHeader
        title="Wie soll die KI mit Preisen umgehen?"
        description="Konservativ ist meist besser — selbst gut gepflegte Listen veralten."
      />
      <div className="space-y-2">
        <Label>Preisrichtlinie</Label>
        <div className="grid gap-2 sm:grid-cols-3">
          {(
            [
              { v: 'mention', t: 'Genaue Preise nennen', sub: 'KI darf konkrete Beträge sagen' },
              { v: 'mention_range', t: 'Nur Preisspannen', sub: 'z.B. „ab 35 €" / „zwischen 60-80 €"' },
              { v: 'no_mention', t: 'Keine Preise nennen', sub: 'Ein Mensch klärt das' },
            ] as const
          ).map((opt) => (
            <button
              key={opt.v}
              type="button"
              onClick={() => update('pricingPolicy', opt.v)}
              className={cn(
                'rounded-md border p-3 text-left transition-colors',
                form.pricingPolicy === opt.v
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/40',
              )}
            >
              <div className="text-sm font-medium">{opt.t}</div>
              <div className="text-xs text-muted-foreground">{opt.sub}</div>
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="ob-pricing-notes">Anmerkungen (optional)</Label>
        <Textarea
          id="ob-pricing-notes"
          rows={4}
          placeholder="z.B. Erstberatung kostenlos, Ratenzahlung möglich, Materialkosten kommen extra hinzu."
          value={form.pricingNotes ?? ''}
          onChange={(e) => update('pricingNotes', e.target.value)}
        />
      </div>
    </div>
  );
}

function BookingStep({ form, update }: { form: OnboardingData; update: StepProps['update'] }) {
  return (
    <div className="max-w-2xl space-y-5">
      <StepHeader
        title="Wie laufen Termine bzw. Anfragen?"
        description="Bestimmt, ob die KI direkt buchen darf oder nur eine Wunsch-Anfrage aufnimmt."
      />
      <div className="space-y-2">
        <Label>Buchungsmodus</Label>
        <div className="grid gap-2 sm:grid-cols-3">
          {(
            [
              { v: 'direct', t: 'Direkt buchen', sub: 'KI legt den Termin im Kalender an' },
              { v: 'request_only', t: 'Anfrage sammeln', sub: 'Mensch bestätigt später' },
              { v: 'no_booking', t: 'Keine Termine', sub: 'Reine Auskunft / Tickets' },
            ] as const
          ).map((opt) => (
            <button
              key={opt.v}
              type="button"
              onClick={() => update('bookingMode', opt.v)}
              className={cn(
                'rounded-md border p-3 text-left transition-colors',
                form.bookingMode === opt.v
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/40',
              )}
            >
              <div className="text-sm font-medium">{opt.t}</div>
              <div className="text-xs text-muted-foreground">{opt.sub}</div>
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="ob-booking-notes">Anmerkungen (optional)</Label>
        <Textarea
          id="ob-booking-notes"
          rows={4}
          placeholder="z.B. Erstkunden brauchen 5 Min Vorgespräch, am Wochenende keine Termine, bei Reklamationen Inhaberin direkt verbinden."
          value={form.bookingNotes ?? ''}
          onChange={(e) => update('bookingNotes', e.target.value)}
        />
      </div>
    </div>
  );
}

function FAQStep({
  form,
  setForm,
}: {
  form: OnboardingData;
  setForm: React.Dispatch<React.SetStateAction<OnboardingData>>;
}) {
  const faq = form.faq ?? [];
  function add() {
    setForm((p) => ({ ...p, faq: [...(p.faq ?? []), { question: '', answer: '' }] }));
  }
  function remove(i: number) {
    setForm((p) => ({ ...p, faq: (p.faq ?? []).filter((_, j) => j !== i) }));
  }
  function patch(i: number, patch: Partial<OnboardingFAQ>) {
    setForm((p) => ({
      ...p,
      faq: (p.faq ?? []).map((f, j) => (i === j ? { ...f, ...patch } : f)),
    }));
  }
  return (
    <div className="max-w-3xl space-y-5">
      <StepHeader
        title="Häufige Fragen (FAQ)"
        description="Gib der KI fertige Antworten für die Fragen, die wirklich oft kommen — sie zitiert sie dann wörtlich."
      />
      <div className="space-y-3">
        {faq.map((f, i) => (
          <div key={i} className="rounded-md border border-border p-3 space-y-2">
            <div className="flex items-start gap-2">
              <Input
                placeholder="Frage (z.B. Bieten Sie Hausbesuche an?)"
                value={f.question}
                onChange={(e) => patch(i, { question: e.target.value })}
              />
              <Button size="icon" variant="ghost" onClick={() => remove(i)}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
            <Textarea
              rows={3}
              placeholder="Antwort"
              value={f.answer}
              onChange={(e) => patch(i, { answer: e.target.value })}
            />
          </div>
        ))}
        <Button size="sm" variant="outline" onClick={add}>
          <Plus className="mr-1.5 h-3.5 w-3.5" /> Frage hinzufügen
        </Button>
      </div>
    </div>
  );
}

function EmergencyStep({ form, update }: { form: OnboardingData; update: StepProps['update'] }) {
  return (
    <div className="max-w-2xl space-y-5">
      <StepHeader
        title="Notfälle & Sondersituationen"
        description="Was zählt für euch als Notfall, und wie soll die KI darauf reagieren?"
      />
      <div className="space-y-1.5">
        <Label htmlFor="ob-emergency-def">Was ist bei euch ein Notfall?</Label>
        <Textarea
          id="ob-emergency-def"
          rows={4}
          placeholder="z.B. Wasserrohrbruch, akuter Zahnschmerz, Tier in Lebensgefahr. Eine kurze Liste reicht."
          value={form.emergencyDefinition ?? ''}
          onChange={(e) => update('emergencyDefinition', e.target.value)}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="ob-emergency-handling">Wie soll die KI bei einem Notfall reagieren?</Label>
        <Textarea
          id="ob-emergency-handling"
          rows={4}
          placeholder="z.B. Sofort an Bereitschafts-Nummer weiterleiten, oder Ticket mit Priorität urgent erstellen und versprechen, dass binnen 30 Min jemand zurückruft."
          value={form.emergencyHandling ?? ''}
          onChange={(e) => update('emergencyHandling', e.target.value)}
        />
      </div>
    </div>
  );
}

function DocumentsStep({ documents }: { documents: OnboardingDocument[] }) {
  const upload = useUploadOnboardingDoc();
  const remove = useDeleteOnboardingDoc();
  const { toast } = useToast();
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  async function onPick(files: FileList | null) {
    if (!files || files.length === 0) return;
    for (const file of Array.from(files)) {
      try {
        await upload.mutateAsync(file);
        toast({
          title: 'Dokument verarbeitet',
          description: `${file.name} wurde gelesen und in die Wissensbasis eingespielt.`,
        });
      } catch (err) {
        toast({
          title: 'Upload fehlgeschlagen',
          description: (err as Error).message,
          variant: 'destructive',
        });
      }
    }
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <div className="max-w-3xl space-y-5">
      <StepHeader
        title="Wissensdokumente hochladen"
        description="PDF oder Word (.docx). Gemini liest den Text aus und macht ihn der KI verfügbar."
      />
      <div className="rounded-md border border-dashed border-border bg-muted/20 p-6 text-center">
        <Upload className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
        <p className="text-sm font-medium">Dateien hierher ziehen oder Button klicken</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Unterstützt: PDF, DOCX. Max 50 MB pro Datei.
        </p>
        <p className="mt-2 text-[11px] text-muted-foreground">
          <strong>Pages-Datei?</strong> Bitte in Pages → Datei → Exportieren als → PDF — Gemini
          unterstützt das Apple-Format nicht direkt.
        </p>
        <Button
          size="sm"
          className="mt-3"
          variant="outline"
          onClick={() => inputRef.current?.click()}
          disabled={upload.isPending}
        >
          {upload.isPending ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Upload className="mr-1.5 h-3.5 w-3.5" />
          )}
          Datei auswählen
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          multiple
          className="hidden"
          onChange={(e) => void onPick(e.target.files)}
        />
      </div>
      {documents.length > 0 && (
        <div className="space-y-2">
          <Label className="text-xs">Hochgeladene Dokumente</Label>
          {documents.map((d) => (
            <div
              key={d.id}
              className="flex items-center justify-between rounded-md border border-border p-3 text-sm"
            >
              <div className="flex min-w-0 items-center gap-2">
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0">
                  <div className="truncate font-medium">{d.fileName}</div>
                  <div className="text-xs text-muted-foreground">
                    {d.status === 'extracting' && 'Wird gelesen …'}
                    {d.status === 'ready' &&
                      `${(d.extractedText ?? '').length.toLocaleString('de-DE')} Zeichen extrahiert`}
                    {d.status === 'error' && (
                      <span className="text-destructive">Fehler: {d.errorMessage}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {d.status === 'extracting' && <Loader2 className="h-4 w-4 animate-spin" />}
                {d.status === 'ready' && <Badge variant="success">OK</Badge>}
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => void remove.mutateAsync(d.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PersonalityStep({ form, update }: { form: OnboardingData; update: StepProps['update'] }) {
  return (
    <div className="max-w-2xl space-y-6">
      <StepHeader
        title="Persönlichkeit & Stil"
        description="Wie soll die KI mit Anrufern reden?"
      />
      <div className="space-y-2">
        <Label>Anrede</Label>
        <div className="grid gap-2 sm:grid-cols-2">
          {(
            [
              { v: 'sie', t: 'Sie', sub: 'Formelle Distanz, Standard für die meisten Branchen' },
              { v: 'du', t: 'Du', sub: 'Persönlich-direkt, gut für Friseur, Gastro, Wellness' },
            ] as const
          ).map((opt) => (
            <button
              key={opt.v}
              type="button"
              onClick={() => update('addressing', opt.v)}
              className={cn(
                'rounded-md border p-3 text-left transition-colors',
                form.addressing === opt.v
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/40',
              )}
            >
              <div className="text-sm font-medium">{opt.t}</div>
              <div className="text-xs text-muted-foreground">{opt.sub}</div>
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <Label>Tonalität</Label>
        <div className="grid gap-2 sm:grid-cols-2">
          {(
            [
              { v: 'formal', t: 'Formell', sub: 'Konservativ, präzise — z.B. Kanzlei, Arztpraxis' },
              { v: 'casual', t: 'Locker', sub: 'Freundlich-warm — z.B. Friseur, Restaurant' },
            ] as const
          ).map((opt) => (
            <button
              key={opt.v}
              type="button"
              onClick={() => update('tonality', opt.v)}
              className={cn(
                'rounded-md border p-3 text-left transition-colors',
                form.tonality === opt.v
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/40',
              )}
            >
              <div className="text-sm font-medium">{opt.t}</div>
              <div className="text-xs text-muted-foreground">{opt.sub}</div>
            </button>
          ))}
        </div>
      </div>
      <div className="rounded-md border border-border bg-muted/20 p-3">
        <div className="mb-2 flex items-center justify-between">
          <Label htmlFor="ob-consultative">Beratende Tätigkeit</Label>
          <Switch
            id="ob-consultative"
            checked={!!form.consultativeMode}
            onCheckedChange={(c) => update('consultativeMode', c)}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          {form.consultativeMode
            ? 'KI darf aktiv beraten, Empfehlungen aussprechen und Anliegen kurz zusammenfassen, bevor sie antwortet.'
            : 'KI nimmt sachlich auf, gibt KEINE eigenen Empfehlungen, der Mensch entscheidet.'}
        </p>
      </div>
    </div>
  );
}

function SummaryStep({
  form,
  documents,
  onApply,
  pending,
}: {
  form: OnboardingData;
  documents: OnboardingDocument[];
  onApply: () => void;
  pending: boolean;
}) {
  const filled = (v: string | undefined) => (v && v.trim() ? v : null);
  return (
    <div className="max-w-3xl space-y-6">
      <StepHeader
        title="Zusammenfassung"
        description="Prüf nochmal kurz, dann komprimiert Gemini deine Antworten in System-Prompt, Wissensbasis, Begrüßung und Verabschiedung."
      />
      <div className="space-y-4 rounded-md border border-border p-4 text-sm">
        <Row label="Firma">
          {filled(form.companyName) ?? <em className="text-muted-foreground">nicht gesetzt</em>}
        </Row>
        <Row label="Branche">
          {filled(form.industry) ?? <em className="text-muted-foreground">—</em>}
        </Row>
        <Row label="Adresse">
          {[form.addressStreet, form.addressZip, form.addressCity]
            .filter(Boolean)
            .join(', ') || <em className="text-muted-foreground">—</em>}
        </Row>
        <Row label="Öffnungszeiten">
          {(form.openingHours ?? []).filter((h) => !h.closed).length} Tage geöffnet
        </Row>
        <Row label="Sonderzeiten">
          {(form.specialClosures ?? []).length} Einträge
        </Row>
        <Row label="Angebot">
          {(form.offerings ?? []).length} Positionen
        </Row>
        <Row label="FAQ">
          {(form.faq ?? []).length} Frage-Antwort-Paare
        </Row>
        <Row label="Dokumente">
          {documents.filter((d) => d.status === 'ready').length} verarbeitet ·{' '}
          {documents.filter((d) => d.status === 'extracting').length} laufend
        </Row>
        <Row label="Buchungsmodus">{form.bookingMode ?? '—'}</Row>
        <Row label="Preisrichtlinie">{form.pricingPolicy ?? '—'}</Row>
        <Row label="Stil">
          {form.addressing === 'du' ? 'duzt' : 'siezt'} ·{' '}
          {form.tonality === 'formal' ? 'formell' : 'locker'} ·{' '}
          {form.consultativeMode ? 'beratend' : 'sachlich'}
        </Row>
      </div>
      <div className="rounded-md border border-primary/30 bg-primary/5 p-4">
        <h3 className="mb-1 text-sm font-medium">Was passiert beim Übernehmen?</h3>
        <ul className="space-y-1 text-xs text-muted-foreground">
          <li>• Gemini liest alle Antworten + extrahierten Dokument-Texte.</li>
          <li>• Wissensbasis wird strukturiert geschrieben (max ~18.000 Zeichen).</li>
          <li>• Begrüßung + Verabschiedung werden vorgeschlagen — du kannst sie danach im KI-Modell-Tab feinschleifen.</li>
          <li>• Konversations-Stil wird passend zu deiner Persönlichkeits-Auswahl gesetzt.</li>
          <li>• Bestehende Werte in den anderen Settings-Tabs bleiben unangetastet.</li>
        </ul>
      </div>
      <Button onClick={onApply} disabled={pending} size="lg" className="w-full">
        {pending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkle className="mr-2 h-4 w-4" />}
        Komprimieren & übernehmen
      </Button>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-32 shrink-0 text-xs uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="flex-1">{children}</div>
    </div>
  );
}

// `X` and `AlertTriangle`, `Coins`, `ListChecks`, `UserCog` already imported above
// for the icon set; the unused `X` is kept for the close-button slot if a
// future revision wants an inline close icon inside the dialog footer.
void X;
